import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { IDENTIFIED_BY_VALUES } from '#~/analytics/constants/device-identification.js';
import type {
  BasicControlsOutcome,
  DeviceIdentificationProperties,
  DriverType,
  IdentifiedBy,
  NotListedOpenedFrom,
  NotListedOutcome,
  SearchNoMatchesField
} from '#~/analytics/constants/device-identification.js';
import type { EventProperties } from '#~/analytics/types/properties.js';
import type { AnalyticsTrackFn } from './types.js';

// ENG-4833 — "how was this camera identified?". This module owns the pure
// resolver that turns the add wizard's identification state into the six
// snake_case props on the camera-save success events, plus typed trackers for
// the three identification events. Wire values live in
// constants/device-identification.ts.
//
// Per rule: preserve-historical-telemetry-during-renames (ai.knowledge/platform)
// — the props extend the live `device_add` / `multi_add_device_added` events;
// the never-fired `successfully_add_camera` name the ticket uses is
// deliberately left alone (open product decision, not a code one). The
// `identified_by` enum itself WAS renamed to match the ticket verbatim
// (ENG-4833): `not_listed_typed` → `not_listed`, `detected_then_changed` →
// `searched`, and `skipped` was dropped. Because `identified_by` is persisted
// as a free string on StudioDevice field 15 (save.ts `toPersistedIdentification`,
// protobuf/device.ts), a record written by an older build can still carry an
// old spelling; `resolveIdentifiedBy` maps those to the new values instead of
// minting a stale PostHog bucket, so historical rows keep reading correctly.

export type {
  BasicControlsOutcome,
  DeviceIdentificationProperties,
  DriverType,
  IdentifiedBy,
  NotListedOpenedFrom,
  NotListedOutcome,
  SearchNoMatchesField
};

/**
 * The add store's `identification` slice (ENG-4832) as the resolver reads it.
 * Every field is optional: a camera saved without ever touching the
 * identification step has none of them, and `identified_by` is then derived
 * from the saved model (see `resolveIdentifiedBy`).
 * `| undefined` is explicit so a store that clears a field by writing
 * `undefined` type-checks under exactOptionalPropertyTypes.
 */
export interface DeviceIdentificationState {
  identifiedBy?: IdentifiedBy | undefined;
  typedBrand?: string | undefined;
  typedModel?: string | undefined;
  detectedBrand?: string | undefined;
  detectedModel?: string | undefined;
}

/** The registry facts the resolver needs about the saved model. */
export interface DeviceIdentificationModel {
  /** Brand-generic / "Other" model — no curated driver. */
  generic: boolean;
  /** Registry marks the model as not yet supported. */
  notYetSupported?: boolean | undefined;
}

export interface ResolveIdentificationPropsInput {
  identification: DeviceIdentificationState;
  /** The saved registry model; undefined when no model was resolved. */
  model?: DeviceIdentificationModel | undefined;
  /**
   * False when the operator picked No Control. Accepted so existing call sites
   * keep compiling, but NOT read: per ENG-4833 `driver_type` is `own | generic`
   * and No Control does not change which driver the model runs on.
   */
  hasControl?: boolean | undefined;
}

// Runtime guard for identified_by. The type already restricts callers, but the
// value can come from persisted device/store state written by an older build
// (StudioDevice field 15 is a free string on the wire).
const KNOWN_IDENTIFIED_BY: ReadonlySet<string> = new Set(IDENTIFIED_BY_VALUES);

const isIdentifiedBy = (value: unknown): value is IdentifiedBy =>
  typeof value === 'string' && KNOWN_IDENTIFIED_BY.has(value);

// Spellings persisted by builds before the ENG-4833 rename, mapped onto the
// ticket's enum. `skipped` is deliberately absent: it meant "nothing recorded"
// and takes the same model-derived fallback as an unknown/absent value.
const LEGACY_IDENTIFIED_BY: Readonly<Record<string, IdentifiedBy>> = {
  not_listed_typed: 'not_listed',
  detected_then_changed: 'searched'
};

// '' (never undefined) for an absent name, so a PostHog breakdown buckets
// "none" instead of dropping the row. Trimmed so whitespace-only free text from
// the "not listed" inputs also reads as absent.
const nameOrEmpty = (value: string | undefined): string => value?.trim() ?? '';

// ENG-4833: `own | generic`, resolved from the model alone. No Control is not a
// driver type — a curated model stays `own` even when control is switched off.
const resolveDriverType = (model: DeviceIdentificationModel | undefined): DriverType => {
  // No resolved model, a brand-generic/"Other" model, or a not-yet-supported
  // model all run on the generic driver.
  if (!model || model.generic || model.notYetSupported === true) {
    return 'generic';
  }
  return 'own';
};

const resolveIdentifiedBy = (
  identifiedBy: IdentifiedBy | undefined,
  model: DeviceIdentificationModel | undefined
): IdentifiedBy => {
  // `identifiedBy` is typed, but a persisted value from an older build may be a
  // legacy spelling (mapped) or anything else (fallback) — treat it as unknown.
  const stored: unknown = identifiedBy;
  let base: IdentifiedBy;
  if (isIdentifiedBy(stored)) {
    base = stored;
  } else if (typeof stored === 'string' && stored in LEGACY_IDENTIFIED_BY) {
    base = LEGACY_IDENTIFIED_BY[stored] as IdentifiedBy;
  } else {
    // Fallback for absent / `skipped` / unknown (ENG-4833 has no "skipped"
    // bucket): a curated registry model can only have been reached through
    // the brand/model list, so it reads as `searched`; the generic Other/Other
    // catch-all (or no model at all) means the camera was never found in the
    // list, so it reads as `not_listed`.
    base = model && !model.generic ? 'searched' : 'not_listed';
  }
  // Search and the not-yet-supported list share one picker, so the store may
  // only know "searched" (with or without a detected brand). The registry flag
  // on the saved model is what tells the two apart.
  if (
    (base === 'searched' || base === 'brand_detected_then_searched') &&
    model?.notYetSupported === true
  ) {
    return 'not_yet_supported_picked';
  }
  return base;
};

/**
 * Resolve the six ENG-4833 identification props for a camera save.
 *
 * Pure: no store, registry or analytics access. The add wizard calls it next
 * to its existing `catalog` resolution and spreads the result into
 * `trackDeviceAddSuccess` and the `multi_add_device_added` emit.
 */
export const resolveIdentificationProps = (
  input: ResolveIdentificationPropsInput
): DeviceIdentificationProperties => {
  const { identification, model } = input;
  return {
    identified_by: resolveIdentifiedBy(identification.identifiedBy, model),
    detected_brand: nameOrEmpty(identification.detectedBrand),
    detected_model: nameOrEmpty(identification.detectedModel),
    typed_brand: nameOrEmpty(identification.typedBrand),
    typed_model: nameOrEmpty(identification.typedModel),
    driver_type: resolveDriverType(model)
  };
};

/** `device_search_no_matches` (ENG-4831). */
export interface DeviceSearchNoMatchesProperties extends EventProperties {
  /** The search text that returned zero results. */
  search_text: string;
  search_field: SearchNoMatchesField;
  /** The brand a model search is scoped to; '' for the brand field. */
  brand_name: string;
}

/** `device_not_listed_closed` (ENG-4832): fired every time either pop-up closes. */
export interface DeviceNotListedClosedProperties extends EventProperties {
  opened_from: NotListedOpenedFrom;
  outcome: NotListedOutcome;
  /** True when the "Did you mean" button was visible when the pop-up closed. */
  did_you_mean_shown: boolean;
}

/** `device_basic_controls_closed` (ENG-4925, Board D): the camera the notice named. */
export interface DeviceBasicControlsClosedProperties extends EventProperties {
  brand_name: string;
  model_name: string;
  outcome: BasicControlsOutcome;
}

// Same factory shape as createDeviceLifecycleTracker: the host injects its
// AnalyticsTrackFn, so call sites stay independent of the provider stack and
// tests stub one function. None of these events is device-scoped (they
// describe the search and the dialogs, not a camera), so they are not in
// DEVICE_EVENTS and carry no cohort ids.
export const createDeviceIdentificationTracker = (track: AnalyticsTrackFn) => ({
  trackDeviceSearchNoMatches: (properties: DeviceSearchNoMatchesProperties): void => {
    track(EVENT_NAMES.DEVICE_SEARCH_NO_MATCHES, EVENT_CATEGORIES.DEVICE, properties);
  },

  trackDeviceNotListedClosed: (properties: DeviceNotListedClosedProperties): void => {
    track(EVENT_NAMES.DEVICE_NOT_LISTED_CLOSED, EVENT_CATEGORIES.DEVICE, properties);
  },

  trackDeviceBasicControlsClosed: (properties: DeviceBasicControlsClosedProperties): void => {
    track(EVENT_NAMES.DEVICE_BASIC_CONTROLS_CLOSED, EVENT_CATEGORIES.DEVICE, properties);
  }
});
